/**
 * Client-side search over a list the section has already loaded.
 *
 * The governance lists (users, attendees across events) and an event's
 * attendee directory are read in full, so filtering happens in memory.
 * Matching ignores case and accents, so "muller" finds "Müller", and a query
 * of several words matches only a row that contains every word, in any of its
 * fields and in any order.
 */

type SearchField = string | null | undefined;

export function normalizeForSearch(value: string): string {
  return value
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase();
}

/**
 * Builds a predicate for one query, so the query is normalised once rather
 * than once per row.
 */
export function searchMatcher(
  query: string,
): (fields: readonly SearchField[]) => boolean {
  const terms = normalizeForSearch(query).split(/\s+/).filter(Boolean);
  if (terms.length === 0) return () => true;
  return (fields) => {
    // Joined on a newline so a term never matches across two fields: terms
    // are split on whitespace and cannot contain one.
    const haystack = normalizeForSearch(
      fields.filter((field): field is string => Boolean(field)).join("\n"),
    );
    return terms.every((term) => haystack.includes(term));
  };
}
